import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { close, menu } from "../assets";
import { navLinks } from "../data/navLinksData";
import { FaChevronDown } from "react-icons/fa";

const NavItem = ({
  nav,
  dropdownOpen,
  handleDropdownToggle,
  closeDropdown,
  isMobile = false,
  location,
  handleMobileNavClick,
}) => {
  const isActive = location.pathname === nav.link;
  const isSubActive = nav.subLinks?.some((s) => s.link === location.pathname);
  const open = dropdownOpen === nav.id;

  const baseClasses = isMobile
    ? "rounded-md block px-3 py-1 text-base font-medium transition-colors duration-200 font-body"
    : "rounded-md px-3 py-1 text-sm font-medium transition-colors duration-200 font-body";

  const activeClasses = "bg-sky-700 text-white font-body";
  const hoverClasses = "text-sky-700 hover:bg-sky-700 hover:text-white font-body";

  return (
    <div className="relative">
      {nav.subLinks ? (
        <>
          <button
            onClick={() => handleDropdownToggle(nav.id)}
            className={`flex items-center justify-between w-full gap-1 ${baseClasses} ${open || isSubActive ? activeClasses : hoverClasses
              }`}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={`dropdown-${nav.id}`}
            type="button"
          >
            {nav.title}
            <FaChevronDown
              className={`transition-transform duration-300 ${open ? "rotate-180" : "rotate-0"
                }`}
              aria-hidden="true"
            />
          </button>

          {open && (
            <div
              id={`dropdown-${nav.id}`}
              role="menu"
              className={`${isMobile
                  ? "space-y-2 pl-4"
                  : "absolute mt-2 w-64 bg-white shadow-lg z-10 md:p-1"
                }`}
            >
              {nav.subLinks.map((subLink) => (
                <Link
                  key={subLink.id}
                  to={subLink.link}
                  onClick={
                    isMobile
                      ? handleMobileNavClick // ✅ no setTimeout
                      : closeDropdown
                  }
                  className={`flex items-center ${isMobile ? "" : "gap-6 px-2 py-3 text-sm"
                    } ${location.pathname === subLink.link
                      ? activeClasses
                      : hoverClasses
                    } px-1 py-1 text-sm font-medium transition-colors duration-200`}
                  role="menuitem"
                  tabIndex={0}
                  aria-current={location.pathname === subLink.link ? "page" : undefined}
                >
                  {!isMobile && subLink.icon}
                  <span>{subLink.title}</span>
                </Link>
              ))}
            </div>
          )}
        </>
      ) : (
        <Link
          to={nav.link || "#"}
          onClick={isMobile ? handleMobileNavClick : closeDropdown}
          className={`${baseClasses} ${isActive ? activeClasses : hoverClasses
            }`}
          aria-current={isActive ? "page" : undefined}
        >
          {nav.title}
        </Link>
      )}
    </div>
  );
};

const Navbar = () => {
  const [toggle, setToggle] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const location = useLocation();
  const dropdownRef = useRef(null);

  const handleDropdownToggle = (id) => {
    setDropdownOpen((prev) => (prev === id ? null : id));
  };

  const closeDropdown = () => {
    setDropdownOpen(null);
  };

  const handleMobileNavClick = () => {
    setToggle(false);
    setDropdownOpen(null);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setDropdownOpen(null);
      }
    };

    if (window.innerWidth >= 1024) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav
      className="bg-white sticky top-0 z-50 border-b border-gray-200 font-sans"
      aria-label="Hauptnavigation"
    >
      <div className="mx-auto max-w-6xl px-6 sm:px-6 lg:px-4">
        <div className="flex h-20 items-center justify-between relative">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img
              className="h-12 w-auto"
              src="/Logo.png"
              alt="Vereinslogo Angelfreunde 1866 e.V."
            />
            <span className="font-bold tracking-tight text-sky-900 font-heading">
              Angelfreunde 1866 e.V.
            </span>
          </Link>

          {/* Desktop Nav */}
          <div
            ref={dropdownRef}
            className="hidden lg:flex flex-1 items-center justify-end space-x-4"
          >
            {navLinks.map((nav) => (
              <NavItem
                key={nav.id}
                nav={nav}
                dropdownOpen={dropdownOpen}
                handleDropdownToggle={handleDropdownToggle}
                closeDropdown={closeDropdown}
                location={location}
              />
            ))}
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <button
              onClick={() => setToggle(!toggle)}
              className="relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:text-sky-700 focus:outline-none"
              aria-label={toggle ? "Menü schließen" : "Menü öffnen"}
              aria-controls="mobile-nav"
              aria-expanded={toggle}
              type="button"
            >
              <img
                src={toggle ? close : menu}
                alt={toggle ? "Menü schließen" : "Menü öffnen"}
                className="h-4 w-6"
              />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      {toggle && (
        <div id="mobile-nav" className="lg:hidden space-y-1 px-2 pb-3 pt-2">
          {navLinks.map((nav) => (
            <NavItem
              key={nav.id}
              nav={nav}
              dropdownOpen={dropdownOpen}
              handleDropdownToggle={handleDropdownToggle}
              closeDropdown={closeDropdown}
              location={location}
              isMobile={true}
              handleMobileNavClick={handleMobileNavClick}
            />
          ))}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
